import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { FormNode } from '../../../../types/form.types'
import { FormGrid } from '../canvas/FormGrid'
import { useFormChrome } from '../formChromeTheme'

export interface GroupElementProps {
  node: FormNode
  renderNode: (n: FormNode) => React.ReactNode
  preview?: boolean
  /** When provided (and not preview), drop zones for moving nodes into this group */
  renderDropZone?: (insertIndex: number) => React.ReactNode
  /** When provided (and not preview), allows toggling collapsed state */
  onCollapseToggle?: (nodeId: string) => void
}

export function GroupElement({ node, renderNode, preview, renderDropZone, onCollapseToggle }: GroupElementProps) {
  const chrome = useFormChrome()
  const children = node.children ?? []
  const collapsed = !!node.collapsed && !preview
  const collapsible = node.collapsible !== false
  const gc = node.style?.groupChrome
  const plain = gc === 'card'
  const accent = gc === 'accent'
  const cardChrome = plain || accent
  // Untitled + no card/subtitle = just a layout box (date+number in a row).
  // Default showBorder/padding would otherwise add ~20px and indent children
  // off the sibling fields sitting in the same parent grid.
  const layoutOnly =
    !cardChrome &&
    node.titleVisible === false &&
    !node.subtitle?.trim() &&
    !collapsed

  const showBorder = layoutOnly ? node.showBorder === true : node.showBorder !== false

  const last = children[children.length - 1]
  const hasFooterBar = accent && last?.type === 'CommandBar'
  const bodyChildren = hasFooterBar ? children.slice(0, -1) : children
  const footerBar = hasFooterBar ? last : null

  const denseGap = cardChrome && node.groupLayout !== 'horizontalAlways' && node.groupLayout !== 'horizontalIfPossible'
  const rowGap = node.groupGap != null ? (node.groupGap / 8) : (denseGap ? 2 : 1)
  const gridRowAlignItems = denseGap ? ('flex-start' as const) : ('center' as const)
  const innerPadding =
    node.groupPadding != null
      ? node.groupPadding / 8
      : layoutOnly
        ? 0
        : cardChrome
          ? 2
          : 1.5

  const showTitle = node.titleVisible !== false
  const showCollapseBtn = !preview && !!onCollapseToggle && collapsible && !layoutOnly
  const showHeader = showTitle || showCollapseBtn || collapsed

  const content = (
    <Box
      className={cardChrome ? 'form-card' : undefined}
      data-testid="form-group"
      {...(layoutOnly ? { 'data-layout-only': 'true' } : {})}
      sx={{
        ...(typeof node.labelColumnWidth === 'number'
          ? { '--form-label-col': `${node.labelColumnWidth}px` }
          : {}),
        width: cardChrome || layoutOnly ? '100%' : undefined,
        border: showBorder ? (cardChrome ? `1px solid ${chrome.cardBorder}` : 1) : 'none',
        borderColor: cardChrome ? undefined : 'divider',
        borderRadius: layoutOnly ? 0 : cardChrome ? '12px' : 1,
        overflow: layoutOnly ? 'visible' : 'hidden',
        bgcolor: layoutOnly ? 'transparent' : cardChrome ? chrome.cardBg : 'background.paper',
        boxShadow: cardChrome ? chrome.cardShadow : undefined,
      }}
    >
      {showHeader && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          px: cardChrome ? 2 : 1,
          py: cardChrome ? 1.25 : 0.5,
          bgcolor: accent ? chrome.accentHeaderBg : plain ? chrome.headerBg : 'action.hover',
          borderBottom: collapsed ? 0 : (showBorder ? 1 : 0),
          borderColor: cardChrome ? chrome.cardBorder : 'divider',
        }}
      >
        {showCollapseBtn && (
          <IconButton size="small" onClick={() => onCollapseToggle(node.id)} aria-label={collapsed ? 'Expand' : 'Collapse'} sx={{ p: 0.25, mr: 0.5, mt: 0.25 }}>
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
        {showTitle && (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: cardChrome ? 13 : (node.style?.fontSize ?? 13),
              fontWeight: cardChrome ? 600 : (node.style?.bold ? 700 : 600),
              fontStyle: node.style?.italic ? 'italic' : undefined,
              color: accent ? chrome.accentTitle : plain ? chrome.title : (node.style?.textColor ?? undefined),
              fontFamily: cardChrome ? chrome.uiFont : undefined,
            }}
          >
            {node.title || node.name}
          </Typography>
          {node.subtitle?.trim() ? (
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                lineHeight: 1.45,
                color: accent ? chrome.accentTitle : chrome.muted,
                opacity: accent ? 0.85 : 1,
                fontFamily: cardChrome ? chrome.uiFont : undefined,
              }}
            >
              {node.subtitle}
            </Typography>
          ) : null}
        </Box>
        )}
        {!showTitle && <Box sx={{ flex: 1, minWidth: 0 }} />}
        {collapsed && (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {children.length} item{children.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
      )}
      {!collapsed && (
        <>
          <Box
            className={cardChrome ? 'form-body' : undefined}
            data-testid="form-group-body"
            sx={{
              p: innerPadding,
              minHeight: !layoutOnly && !preview && bodyChildren.length === 0 ? 48 : undefined,
            }}
          >
            <FormGrid
              nodes={bodyChildren}
              groupLayout={node.groupLayout}
              rowGap={rowGap}
              gridRowAlignItems={gridRowAlignItems}
              renderNode={renderNode}
              renderDropZone={!preview && renderDropZone ? renderDropZone : undefined}
            />
            {bodyChildren.length === 0 && !preview && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                Drop elements here
              </Typography>
            )}
          </Box>
          {footerBar && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                borderTop: `1px solid ${chrome.cardBorder}`,
                bgcolor: chrome.headerBg,
                px: 2,
                py: 1.75,
              }}
            >
              {renderNode(footerBar)}
            </Box>
          )}
        </>
      )}
    </Box>
  )

  if (node.tooltip) {
    return <Tooltip title={node.tooltip}>{content}</Tooltip>
  }

  return content
}
